import React, { PureComponent } from 'react'
import styled from 'styled-components'
import getPalette, { IPalette } from '../../helpers/colors/colors'
import getFont from '../../helpers/fonts/fonts'

const Container = styled.div`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	color: white;
	font-family: ${getFont('secondaryRegular')};
	font-size: inherit;
	line-height: inherit;
	text-align: center;
`

const ScreenReaderOnly = styled.div`
	position: absolute;
	left: -9999px;
	width: 1px;
	height: 1px;
	overflow: hidden;
`

interface ITimerProps {
	totalTime: number
	elapsedTime: number
	onTimeComplete?: () => void
	onTimeUpdate?: (elapsedTime: number) => void
	shouldRun: boolean
}

interface ITimerState {
	initialTime: number
	currentTime: number
	announced20Seconds: boolean
	announced10Seconds: boolean
	announcement: string
}

class Timer extends PureComponent<ITimerProps, ITimerState> {
	private timeoutId: number
	private elapsedTime: number

	static defaultProps = {
		shouldRun: true,
	}

	constructor(props: ITimerProps) {
		super(props)
		this.timeoutId = 0
		// Elapsed time is being set only in the constructor instead of using the passed down prop
		// This way it doesn't get updated every time a new game state arrives
		// This is to avoid showing the time updating constantly because of latency between server and clients
		this.elapsedTime = props.elapsedTime || 0
	}

	state = {
		initialTime: Date.now(),
		currentTime: Date.now(),
		announced20Seconds: false,
		announced10Seconds: false,
		announcement: '',
	}

	componentDidMount() {
		const initialRemainingSeconds = Math.ceil((this.props.totalTime - this.elapsedTime) / 1000)
		this.setState({
			announcement: `${initialRemainingSeconds} seconds remaining`,
		})

		if (this.props.shouldRun) {
			this.timeoutId = window.setTimeout(this.countDown, 10)
		}
	}

	checkThresholdAnnouncements = (remainingSeconds: number) => {
		const { announced20Seconds, announced10Seconds } = this.state

		if (remainingSeconds <= 20 && remainingSeconds > 10 && !announced20Seconds) {
			this.setState({
				announced20Seconds: true,
				announcement: '20 seconds remaining',
			})
		} else if (remainingSeconds <= 10 && !announced10Seconds) {
			this.setState({
				announced10Seconds: true,
				announcement: '10 seconds remaining',
			})
		}
	}

	countDown = () => {
		const currentTime = Date.now()
		if (currentTime - this.state.initialTime >= this.props.totalTime - this.elapsedTime) {
			// When countdown is complete we're setting the time to 0 and submitting an empty answer to the server
			this.setState(() => {
				return {
					currentTime: this.state.initialTime + (this.props.totalTime - this.elapsedTime),
				}
			})
			if (this.props.onTimeComplete) {
				this.props.onTimeComplete()
			}
		} else {
			const remainingSeconds = Math.ceil(
				(this.props.totalTime - this.elapsedTime - (currentTime - this.state.initialTime)) / 1000
			)
			this.checkThresholdAnnouncements(remainingSeconds)
			this.setState(() => {
				return {
					currentTime,
				}
			})
			this.timeoutId = window.setTimeout(this.countDown, 10)
			if (this.props.onTimeUpdate) {
				this.props.onTimeUpdate(this.state.currentTime - this.state.initialTime + this.elapsedTime)
			}
		}
	}

	render() {
		const palette: IPalette = getPalette()
		const elapsedTime = Math.ceil(
			(this.props.totalTime - this.elapsedTime - (this.state.currentTime - this.state.initialTime)) / 1000
		)
		let timerColor

		if (elapsedTime > 20) {
			timerColor = palette.success
		} else if (elapsedTime > 10) {
			timerColor = palette.warning
		} else {
			timerColor = palette.alert
		}

		const textColor = palette.white

		const ariaLabel = `${elapsedTime} seconds remaining`

		return (
			<>
				<Container
					style={{
						backgroundColor: timerColor,
						color: textColor,
					}}
					role="timer"
				>
					<ScreenReaderOnly as="span">{ariaLabel}</ScreenReaderOnly>
					<span aria-hidden="true">{elapsedTime}</span>
				</Container>
				<ScreenReaderOnly
					role="alert"
					aria-live="assertive"
					aria-atomic="true"
				>
					{this.state.announcement}
				</ScreenReaderOnly>
			</>
		)
	}

	componentWillUnmount() {
		window.clearTimeout(this.timeoutId)
	}
}

export default Timer
